import { AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';

import Header from 'components/Header/Header';
import Map from 'components/Map/Map';
import Sidebar from 'components/Sidebar/Sidebar';
import { useCopyStore, useGlobalStore } from 'store';
import { ReactComponent as InfoIcon } from 'svgs/infoCircle.svg';
import gtm, { actions, categories } from 'u9/utils/gtm';
import { INTRODUCTION_CHAPTERS } from 'utils/config';
import { ROUTES } from 'utils/routes';

import * as Styled from './Layout.styles';

export interface LayoutProps {
  children: React.ReactNode | React.ReactNode[];
}

const defaultProps: Partial<LayoutProps> = {};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const {
    currentChapter,
    isSidebarExpanded,
    isLandingVisible,
    isCardExpanded,
    setCurrentTheme,
    setSidebarExpanded,
    setCardExpanded,
  } = useGlobalStore();
  const {
    copy: { global, accessibility },
  } = useCopyStore();
  const router = useRouter();
  const timeout = useRef<number>(null);

  useEffect(() => {
    if (!isCardExpanded && router.pathname !== ROUTES.HOME) {
      const isIntroductionChapter =
        INTRODUCTION_CHAPTERS.includes(currentChapter);
      if (timeout.current) window.clearTimeout(timeout.current);
      timeout.current = window.setTimeout(
        () => {
          timeout.current = null;
          setCardExpanded(true);
        },
        isIntroductionChapter ? 3000 : 600
      );
    }
    return () => {
      if (timeout.current) window.clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChapter, router]);

  useEffect(() => {
    router.beforePopState(({ as }) => {
      if (as !== router.asPath && as === ROUTES.HOME) setCurrentTheme(null);
      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  const toggleSidebar = () => {
    setSidebarExpanded(!isSidebarExpanded);
    gtm.trackEvent(
      categories.about,
      actions.click,
      !isSidebarExpanded ? 'Open' : 'Close'
    );
  };

  return (
    <Styled.Container>
      <Sidebar />
      <Styled.ActionButton
        onClick={toggleSidebar}
        isLandingVisible={isLandingVisible}
        aria-hidden={isSidebarExpanded}
        aria-expanded={isSidebarExpanded}
      >
        <Styled.Link
          isLandingVisible={isLandingVisible}
          dangerouslySetInnerHTML={{ __html: global?.common?.aboutBtn }}
        />
        <InfoIcon aria-label={accessibility?.shared?.aboutBtn} />
      </Styled.ActionButton>
      <div aria-hidden={isSidebarExpanded}>
        {!isLandingVisible && <Header />}
        <AnimatePresence exitBeforeEnter={true} initial={true}>
          {children}
        </AnimatePresence>
        <AnimatePresence exitBeforeEnter={true} initial={true}>
          {!isLandingVisible ? <Map /> : null}
        </AnimatePresence>
      </div>
      <Styled.Mask
        initial={{ opacity: 0 }}
        animate={isSidebarExpanded ? { opacity: 0.7 } : { opacity: 0 }}
        transition={{ duration: 0.6 }}
      />
    </Styled.Container>
  );
};

Layout.defaultProps = defaultProps;

export default Layout;
